import { useCallback, useMemo, useReducer, useState } from 'react';

import {
  ChakraFlex,
  ChakraVStack,
  Divider,
  InsuranceReviewCard,
  InsuranceReviewModal,
  InsuranceReviewType,
  IntakeSurveyCard,
  MedicalReviewCard,
  MedicalReviewModal,
  SkeletonLoader,
  TagProps,
  Text,
} from '@equip.health/ui';
import { InsuranceReviewState } from '@equip.health/ui/lib/types/src/maud/modals/InsuranceReview/InsuranceReviewModal';
import {
  DiagnosisOptions,
  MedicalReviewState,
} from '@equip.health/ui/lib/types/src/maud/modals/MedicalReview/MedicalReviewModal';
import { useHistory } from 'react-router-dom';

import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import {
  InquiryDetailViewSurveyResponseStatusDefinition,
  NOT_CREATED,
} from '~/lib/constants/survey/SurveyResponseStatus';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { toUTCMidnight } from '~/lib/util/date.util';
import {
  createTagProps,
  findCodeByDescription,
  isEqualIgnoreNil,
  reducer,
  toDate,
  toDescriptions,
  toStatusOptions,
} from '~/lib/util/inquiry.util';
import { Path } from '~/routes/constants';
import {
  useGetAssessmentSummariesByInquiryIdQuery,
  useGetSurveysQuery,
} from '~/store/services/assessments';
import {
  useGetDiagnosisCodesSearchQuery,
  useLazyGetDiagnosisCodesSearchQuery,
} from '~/store/services/clinicalData';
import {
  useGetCoverageReviewStatusesQuery,
  useGetCoverageReviewTypesQuery,
  useGetInquiryReviewsQuery,
  useGetMedicalReviewNotClearedReasonsQuery,
  useGetMedicalReviewStatusesQuery,
  useGetMedicalReviewTypesQuery,
  useUpdateInquiryInsuranceReviewMutation,
  useUpdateInquiryMedicalReviewMutation,
} from '~/store/services/inquiry';

import { getIntakeAssessment } from '../viewUtil';
import { MedicalStatusOptions } from '~/lib/constants/inquiry.constants';

interface IntakeSurveyCardProps {
  completedDate: Date;
  id: string;
  onViewResults: () => void;
  startedDate: Date;
  status: TagProps;
}

export interface ReviewsSectionProps {
  id: string;
  inquiryId: string;
  patientId: string;
  shouldInvalidate?: boolean;
  version: string;
}

export const ReviewsSection = ({
  id,
  inquiryId,
  patientId,
  shouldInvalidate = false,
  version,
}: ReviewsSectionProps) => {
  const TOP_LEVEL_ID = `${id}__reviews`;

  const { showErrorToast } = useErrorToast();

  const { push } = useHistory();

  const { setComponentVisibility } = useComponentPortalContext();

  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState<boolean>(false);

  const [isInsuranceReviewModalOpen, setIsInsuranceReviewModalOpen] =
    useState<boolean>(false);
  const [isMedicalReviewModalOpen, setIsMedicalReviewModalOpen] =
    useState<boolean>(false);

  const { data: coverageStatusEnums } = useGetCoverageReviewStatusesQuery();
  const { data: coverageReviewTypeEnums } = useGetCoverageReviewTypesQuery();
  const { data: medicalNotClearedReasonEnums } =
    useGetMedicalReviewNotClearedReasonsQuery();
  const { data: medicalStatusEnums } = useGetMedicalReviewStatusesQuery();
  const { data: medicalReviewTypeEnums } = useGetMedicalReviewTypesQuery();

  const { data: assessmentSummariesResponse } =
    useGetAssessmentSummariesByInquiryIdQuery(inquiryId);

  useGetSurveysQuery({
    filterBy: { surveyName: SurveyName.ADMISSIONS_INTAKE },
  });

  const closeDrawerAndGoToPatientChart = useCallback(() => {
    setComponentVisibility({
      component: PortalComponent.INQUIRY_DETAIL_DRAWER,
      data: null,
      isVisible: false,
    });
    push(`${Path.MAUD_PATIENTS}/${patientId}/assessments`);
  }, [patientId]);

  const intakeSurveyCardProps = useMemo<IntakeSurveyCardProps>(() => {
    const intakeAssessment = getIntakeAssessment(
      assessmentSummariesResponse?.data?.assessments,
    );

    const { endDate, startDate, status } = intakeAssessment ?? {};

    const { color: statusColor, label: statusLabel } =
      InquiryDetailViewSurveyResponseStatusDefinition[
        status?.name ?? NOT_CREATED
      ] ?? {};

    return {
      completedDate: !endDate ? undefined : new Date(endDate),
      id: TOP_LEVEL_ID,
      onViewResults: closeDrawerAndGoToPatientChart,
      startedDate: !startDate ? undefined : new Date(startDate),
      status: {
        color: statusColor,
        id: TOP_LEVEL_ID,
        value: statusLabel,
      },
    };
  }, [
    assessmentSummariesResponse,
    closeDrawerAndGoToPatientChart,
    TOP_LEVEL_ID,
  ]);

  const { data: inquiryReviewResponse } = useGetInquiryReviewsQuery(inquiryId, {
    skip: !inquiryId,
  });
  const { data: admissionsDiagnosisCodes } = useGetDiagnosisCodesSearchQuery({
    isAdmissions: true,
  });

  const { coverage, medical } = inquiryReviewResponse ?? {};

  const [
    triggerUpdateInsuranceReview,
    { isLoading: isUpdateInsuranceReviewLoading },
  ] = useUpdateInquiryInsuranceReviewMutation();

  const [
    triggerUpdateMedicalReview,
    { isLoading: isUpdateMedicalReviewLoading },
  ] = useUpdateInquiryMedicalReviewMutation();

  // MedicalReviewModal was written to support asynchronous loading of pages of diagnoses. The reality is that we are
  // only using those with `isAdmissions==true` which is a very short list.
  const diagnosisOptions = useMemo<DiagnosisOptions>(
    () => ({
      inputValue: '',
      isLoading: false,
      onLoadMore: () => {},
      onSearch: () => {},
      options: (admissionsDiagnosisCodes ?? []).map((diagnosis) => {
        return {
          label: (
            <ChakraVStack alignItems="start" key={diagnosis.code} spacing="2px">
              <Text
                color="neutral.primary"
                id={`${TOP_LEVEL_ID}__diagnosis-description`}
              >
                {diagnosis.shortDescription}
              </Text>
              <Text
                color="neutral.secondary"
                id={`${TOP_LEVEL_ID}__diagnosis-code`}
                variant="caption"
              >
                {diagnosis.code}
              </Text>
            </ChakraVStack>
          ),
          searchData: diagnosis.shortDescription.toLowerCase(),
          value: diagnosis.shortDescription,
        };
      }),
    }),
    [admissionsDiagnosisCodes],
  );

  const coverageStatusOptions = useMemo(() => {
    return toStatusOptions(coverageStatusEnums);
  }, [coverageStatusEnums]);

  const medicalStatusOptions = useMemo(() => {
    return toStatusOptions(medicalStatusEnums);
  }, [medicalStatusEnums]);

  const coverageReviewStatus = useMemo<TagProps>(() => {
    return createTagProps(coverage?.status?.code, coverageStatusEnums);
  }, [coverageStatusEnums, coverage?.status]);

  const medicalReviewStatus = useMemo<TagProps>(() => {
    return createTagProps(medical?.status?.code, medicalStatusEnums);
  }, [medicalStatusEnums, medical?.status]);

  const coverageTypeOptions = useMemo(
    () => toDescriptions(coverageReviewTypeEnums),
    [coverageReviewTypeEnums],
  );

  const medicalTypeOptions = useMemo(
    () => toDescriptions(medicalReviewTypeEnums),
    [medicalReviewTypeEnums],
  );

  const medicalNotClearedReasonOptions = useMemo(
    () => toDescriptions(medicalNotClearedReasonEnums),
    [medicalNotClearedReasonEnums],
  );

  // The sanitized api response
  const coverageInitialState: InsuranceReviewState =
    useMemo<InsuranceReviewState>(() => {
      const { completedDate, requestedDate, status, type } = coverage ?? {};
      return {
        completedDate: toDate(completedDate),
        requestedDate: toDate(requestedDate),
        status: status?.description,
        type: type?.description,
      };
    }, [coverage]);

  const [coverageEditState, updateCoverageEditState] = useReducer<
    (
      state: InsuranceReviewState,
      action: InsuranceReviewState,
    ) => InsuranceReviewState
  >(reducer, {});

  // The sanitized api response
  const medicalInitialState: MedicalReviewState =
    useMemo<MedicalReviewState>(() => {
      const {
        completedDate,
        requestedDate,
        type,
        status,
        notClearedReason,
        preliminaryDiagnosis,
        appointmentDate,
      } = medical ?? {};
      return {
        appointmentDate: toDate(appointmentDate),
        completedDate: toDate(completedDate),
        notClearedReason: notClearedReason?.description,
        preliminaryDiagnosis: preliminaryDiagnosis?.description,
        requestedDate: toDate(requestedDate),
        status: status?.description,
        type: type?.description,
      };
    }, [medical]);

  const [medicalEditState, updateMedicalEditState] = useReducer<
    (
      state: MedicalReviewState,
      action: MedicalReviewState,
    ) => MedicalReviewState
  >(reducer, {});

  const [fetchMatchingDiagnosisCodes] = useLazyGetDiagnosisCodesSearchQuery();

  const isCoverageStateChange = () =>
    !isEqualIgnoreNil(coverageInitialState, coverageEditState);

  const isMedicalNotClearedReasonRequired =
    medicalEditState.status === MedicalStatusOptions.NOT_CLEARED;

  const isMedicalTypeRequired =
    medicalEditState.status !== MedicalStatusOptions.NOT_STARTED;

  const updateInsuranceReview = () => {
    triggerUpdateInsuranceReview({
      completedDate: toUTCMidnight(coverageEditState.completedDate),
      description: coverageEditState.status,
      inquiryId,
      requestedDate: toUTCMidnight(coverageEditState.requestedDate),
      shouldInvalidate,
      status: findCodeByDescription(
        coverageEditState.status,
        coverageStatusEnums,
      ),
      type: findCodeByDescription(
        coverageEditState.type,
        coverageReviewTypeEnums,
      ),
      version,
    })
      .unwrap()
      .then(() => {
        setIsInsuranceReviewModalOpen(false);
      })
      .catch((error) =>
        showErrorToast(
          GlobalErrorCode.UPDATE_INQUIRY_REVIEWS,
          error.status,
          error.data?.message,
        ),
      );
  };

  const updateMedicalReview = async () => {
    let preliminaryDiagnosis = (admissionsDiagnosisCodes ?? []).find(
      (x) => x.shortDescription === medicalEditState.preliminaryDiagnosis,
    );
    if (preliminaryDiagnosis == null) {
      const fetchedCodes = await fetchMatchingDiagnosisCodes({
        searchString: medicalEditState.preliminaryDiagnosis,
      }).unwrap();
      preliminaryDiagnosis = (fetchedCodes ?? []).find(
        (x) => x.shortDescription === medicalEditState.preliminaryDiagnosis,
      );
    }
    triggerUpdateMedicalReview({
      completedDate: toUTCMidnight(medicalEditState.completedDate),
      description: medicalEditState.status,
      inquiryId,
      medicalAppointmentDate: toUTCMidnight(medicalEditState.appointmentDate),
      notClearedReason: findCodeByDescription(
        medicalEditState.notClearedReason,
        medicalNotClearedReasonEnums,
      ),
      preliminaryDiagnosis,
      requestedDate: toUTCMidnight(medicalEditState.requestedDate),
      shouldInvalidate,
      status: findCodeByDescription(
        medicalEditState.status,
        medicalStatusEnums,
      ),
      type: findCodeByDescription(
        medicalEditState.type,
        medicalReviewTypeEnums,
      ),
      version,
    })
      .unwrap()
      .then(() => {
        setIsMedicalReviewModalOpen(false);
      })
      .catch((error) =>
        showErrorToast(
          GlobalErrorCode.UPDATE_INQUIRY_ASSIGNMENTS,
          error.status,
          error.data?.message,
        ),
      );
  };

  const isMedicalStateChange = () =>
    !isEqualIgnoreNil(medicalInitialState, medicalEditState);

  return (
    <SkeletonLoader
      id={TOP_LEVEL_ID}
      isLoaded={!!inquiryReviewResponse}
      width="100%"
    >
      <ChakraFlex
        backgroundColor="neutral.background.primary"
        borderColor="neutral.line"
        borderRadius="12px"
        borderWidth="1px"
        direction="column"
        gap="8px"
        padding="12px"
        width="100%"
      >
        <InsuranceReviewCard
          completedDate={coverageInitialState.completedDate}
          id={TOP_LEVEL_ID}
          onClick={() => {
            updateCoverageEditState(coverageInitialState);
            setIsInsuranceReviewModalOpen(true);
          }}
          requestedDate={coverageInitialState.requestedDate}
          reviewType={coverageInitialState.type as InsuranceReviewType}
          status={coverageReviewStatus}
        />
        <Divider id={TOP_LEVEL_ID} />
        {assessmentSummariesResponse && (
          <>
            <IntakeSurveyCard {...intakeSurveyCardProps} />
            <Divider id={TOP_LEVEL_ID} />
          </>
        )}
        <MedicalReviewCard
          completedDate={medicalInitialState.completedDate}
          diagnosis={medicalInitialState.preliminaryDiagnosis}
          id={TOP_LEVEL_ID}
          onEdit={() => {
            updateMedicalEditState(medicalInitialState);
            setIsMedicalReviewModalOpen(true);
          }}
          startedDate={medicalInitialState.requestedDate}
          status={medicalReviewStatus}
          type={medicalInitialState.type}
        />
        <InsuranceReviewModal
          isOpen={isInsuranceReviewModalOpen}
          isSaveLoading={isUpdateInsuranceReviewLoading}
          onCancel={() => {
            if (isCoverageStateChange()) {
              setIsExitConfirmOpen(true);
            } else {
              setIsInsuranceReviewModalOpen(false);
            }
          }}
          onChange={updateCoverageEditState}
          onConfirm={updateInsuranceReview}
          reviewTypeOptions={coverageTypeOptions}
          state={coverageEditState}
          statusOptions={coverageStatusOptions}
        />
        <MedicalReviewModal
          diagnosisOptions={diagnosisOptions}
          isNotClearedReasonRequired={isMedicalNotClearedReasonRequired}
          isOpen={isMedicalReviewModalOpen}
          isSaveLoading={isUpdateMedicalReviewLoading}
          isTypeRequired={isMedicalTypeRequired}
          notClearedReasons={medicalNotClearedReasonOptions}
          onCancel={() => {
            if (isMedicalStateChange()) {
              setIsExitConfirmOpen(true);
            } else {
              setIsMedicalReviewModalOpen(false);
            }
          }}
          onChange={updateMedicalEditState}
          onConfirm={updateMedicalReview}
          reviewTypeOptions={medicalTypeOptions}
          state={medicalEditState}
          statusOptions={medicalStatusOptions}
        />
        <DiscardChangesModalV2
          isOpen={isExitConfirmOpen}
          onClose={() => setIsExitConfirmOpen(false)}
          onConfirm={() => {
            setIsInsuranceReviewModalOpen(false);
            setIsMedicalReviewModalOpen(false);
            setIsExitConfirmOpen(false);
          }}
        />
      </ChakraFlex>
    </SkeletonLoader>
  );
};
